
import type { ReactElement, ReactNode } from 'react';
import { Field, Input, type InputProps } from '@client/shared/design';
import { firstFieldError } from './zod-adapter.ts';

/** The slice of a TanStack `field` API a text input needs. Kept structural (rather than the
 *  heavyweight generic `FieldApi`) so call sites pass `field` straight from `<form.Field>`. */
export interface TextFieldApi {
  name: string;
  state: { value: string; meta: { errors: readonly unknown[] } };
  handleBlur: () => void;
  handleChange: (value: string) => void;
}

export interface TextFieldProps
  extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'name'> {
  field: TextFieldApi;
  label: ReactNode;
  hint?: ReactNode;
  optional?: boolean;
  /** Class for the field wrapper; `className` still flows through to the underlying `<input>`. */
  fieldClassName?: string;
  /** Runs alongside the field's own change handler: for side effects like clearing a "saved" flag. */
  onValueChange?: (value: string) => void;
}

export function TextField({
  field,
  label,
  hint,
  optional,
  fieldClassName,
  onValueChange,
  ...inputProps
}: TextFieldProps): ReactElement {
  return (
    <Field
      label={label}
      hint={hint}
      optional={optional}
      className={fieldClassName}
      error={firstFieldError(field.state.meta.errors)}
    >
      {(wiring) => (
        <Input
          {...wiring}
          {...inputProps}
          name={field.name}
          value={field.state.value}
          onBlur={field.handleBlur}
          onChange={(e) => {
            field.handleChange(e.target.value);
            onValueChange?.(e.target.value);
          }}
        />
      )}
    </Field>
  );
}
